import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { mfaAPI, sessionAPI, gdprV5API } from '../../lib/api';
import { useAuth } from '../../lib/auth-context';
import { ShieldCheck, KeyRound, Smartphone, Trash2, Download } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminAccountSecurity() {
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mfaSetup, setMfaSetup] = useState(null);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaEnabled, setMfaEnabled] = useState(!!user?.mfa_enabled);

  const refresh = () => {
    setLoading(true);
    sessionAPI
      .list()
      .then((r) => setSessions(r.data?.sessions || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(refresh, []);

  const beginMfa = async () => {
    try {
      const r = await mfaAPI.setup();
      setMfaSetup(r.data);
    } catch {
      toast.error('Failed to start MFA setup');
    }
  };

  const verifyMfa = async () => {
    try {
      await mfaAPI.verify({ code: mfaCode });
      toast.success('Two-factor authentication enabled');
      setMfaEnabled(true);
      setMfaSetup(null);
      setMfaCode('');
    } catch {
      toast.error('Invalid code');
    }
  };

  const disableMfa = async () => {
    const code = window.prompt('Enter your current TOTP code to disable 2FA:');
    if (!code) return;
    try {
      await mfaAPI.disable({ code });
      toast.success('Two-factor authentication disabled');
      setMfaEnabled(false);
    } catch {
      toast.error('Invalid code');
    }
  };

  const revokeSession = async (id) => {
    try { await sessionAPI.revoke(id); refresh(); }
    catch { toast.error('Failed to revoke'); }
  };

  const exportData = async () => {
    if (!user?.id) return;
    try {
      const r = await gdprV5API.exportUserData(user.id);
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `data-export-${user.id}.json`; a.click();
      URL.revokeObjectURL(url);
      toast.success('Data export downloaded');
    } catch {
      toast.error('Export failed');
    }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="admin-account-security-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><ShieldCheck className="w-6 h-6 text-[#B89B5E]" /> Account Security</h1>
        <p className="text-sm text-[#5B616E] mt-1">Two-factor authentication, active sessions, and your personal-data export.</p>
      </div>

      {/* MFA */}
      <Card className="bg-white border-[#E7E0D6]">
        <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><KeyRound className="w-4 h-4" /> Two-factor authentication (TOTP)</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {mfaEnabled ? (
            <>
              <Badge className="bg-[#1F7A5A] text-white">Enabled</Badge>
              <p className="text-xs text-[#5B616E]">An authenticator app is required for sign-in. To rotate, disable and re-enrol.</p>
              <Button variant="outline" onClick={disableMfa}>Disable 2FA</Button>
            </>
          ) : !mfaSetup ? (
            <>
              <p className="text-xs text-[#5B616E]">Add a second factor (Authy, 1Password, Google Authenticator…) for stronger sign-in.</p>
              <Button className="bg-[#121316] text-white hover:bg-[#1B1D22]" onClick={beginMfa}>
                <Smartphone className="w-4 h-4 mr-2" /> Set up TOTP
              </Button>
            </>
          ) : (
            <>
              <p className="text-xs">Add this account to your authenticator using the secret below or scan the URI.</p>
              <code className="block text-[11px] p-2 bg-[#FBF7EF] rounded border border-[#E7E0D6] break-all">{mfaSetup.secret}</code>
              <code className="block text-[11px] p-2 bg-[#FBF7EF] rounded border border-[#E7E0D6] break-all">{mfaSetup.provisioning_uri}</code>
              <div className="flex gap-2 items-center">
                <Input placeholder="6-digit code" value={mfaCode} onChange={(e) => setMfaCode(e.target.value)} className="w-40" />
                <Button onClick={verifyMfa} className="bg-[#121316] text-white hover:bg-[#1B1D22]">Verify and enable</Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Sessions */}
      <Card className="bg-white border-[#E7E0D6]">
        <CardHeader className="pb-2"><CardTitle className="text-sm">Active sessions</CardTitle></CardHeader>
        <CardContent>
          {loading ? <Skeleton className="h-32" /> : (
            <div className="divide-y divide-[#E7E0D6]">
              {sessions.length === 0 && <p className="text-xs text-[#5B616E] py-3">No tracked sessions.</p>}
              {sessions.map((s) => (
                <div key={s.id} className="flex items-center gap-3 py-2 text-sm">
                  <div className="flex-1">
                    <p className="font-medium text-xs">{s.device || s.user_agent?.slice(0, 60) || 'Unknown device'}</p>
                    <p className="text-[10px] text-[#5B616E]">{s.ip_address || ''} · {s.created_at ? new Date(s.created_at).toLocaleString() : ''}</p>
                  </div>
                  <button onClick={() => revokeSession(s.id)} className="text-[#B3261E] hover:bg-red-50 rounded p-1" title="Revoke">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Data export */}
      <Card className="bg-white border-[#E7E0D6]">
        <CardHeader className="pb-2"><CardTitle className="text-sm">Personal data export (GDPR Art. 15)</CardTitle></CardHeader>
        <CardContent>
          <p className="text-xs text-[#5B616E] mb-3">Download a JSON snapshot of every record we hold about your account.</p>
          <Button onClick={exportData} className="bg-[#121316] text-white hover:bg-[#1B1D22]">
            <Download className="w-4 h-4 mr-2" /> Download my data
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
